import { type DataSource, type Entity, type EntityReference } from "@firecms/core";
import { type Tag } from "@one-ai/shared/cms/schema";

import { type CmsLocale } from "../i18n/locales";
import { tagDisplayName } from "./tagLabel";

/**
 * The save-time half of "a retired tag cannot be assigned".
 *
 * The UI half is the article `tags` property's `forceFilter`, restored on the
 * search path by `lib/searchFilter.ts`. This is the half that does not depend
 * on which UI path an editor took: an article saved from an older browser tab,
 * a tag retired between opening the form and saving it, or a future create path
 * that forgets the filter all end here.
 *
 * It is a guard, not a sanitiser. Silently dropping the tags would make a save
 * quietly disagree with the form the editor is looking at; refusing the save
 * with the tags named tells them what to remove.
 */

/** A tag as `fetchEntity` returns it — or `undefined`, for one since deleted. */
type FetchedTag = Entity<Partial<Tag>> | undefined;

/**
 * The names of the retired tags among `tags`, in the order they were assigned.
 *
 * A reference that resolves to nothing is skipped rather than reported: a tag
 * deleted out from under an article is not a retired tag, and
 * `ArticleSchema` is explicit that an unresolvable slug is dropped at
 * render and never fatal.
 */
export const retiredTagNames = (
    tags: FetchedTag[],
    locale: CmsLocale,
): string[] =>
    tags
        .filter((tag): tag is Entity<Partial<Tag>> => Boolean(tag))
        .filter((tag) => tag.values?.retired === true)
        .map((tag) => tagDisplayName(tag, locale));

/**
 * Throws if `references` includes a retired tag, with every offender named.
 *
 * Throwing from `onPreSave` stops the save and shows the message in an error
 * snackbar — the same mechanism `invalidSlug` uses.
 */
export const assertNoRetiredTags = async ({
    references,
    tagPath,
    dataSource,
    locale,
    message,
}: {
    references: EntityReference[] | undefined;
    /**
     * The article's OWN stream's tag collection. Required rather than defaulted
     * to `"tags"`: both streams hold a document "3", so a default would look up
     * the wrong stream's tag and report a retired one as active — or the other
     * way round — with nothing to notice it.
     */
    tagPath: string;
    dataSource: DataSource;
    locale: CmsLocale;
    message: (names: string[]) => string;
}): Promise<void> => {
    if (!references?.length) return;

    const tags = await Promise.all(
        references.map((reference) =>
            dataSource.fetchEntity<Partial<Tag>>({
                path: tagPath,
                entityId: reference.id,
            }),
        ),
    );

    const retired = retiredTagNames(tags, locale);
    if (retired.length > 0) throw new Error(message(retired));
};
